import { NavLink } from "react-router-dom";
import { LayoutDashboard, CalendarDays, Plus, Wrench, Menu } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * MobileBottomNav – 5 slots (2 left · centre action · 2 right). Visible < lg only.
 * Blur/pill surface, safe-area aware, 72px tall, active indicator on top.
 */
const itemClass = (active) =>
  cn(
    "pressable relative flex h-full flex-col items-center justify-center gap-1 rounded-[18px] px-1 text-[11px] font-medium leading-none",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/60",
    active ? "text-primary" : "text-muted-foreground"
  );

const Indicator = () => (
  <span aria-hidden="true" className="absolute left-1/2 top-0 h-[3px] w-8 -translate-x-1/2 rounded-full bg-primary" />
);

const Item = ({ to, end, icon: Icon, label, testid }) => (
  <NavLink to={to} end={end} data-testid={testid} aria-label={label} className={({ isActive }) => itemClass(isActive)}>
    {({ isActive }) => (
      <>
        {isActive && <Indicator />}
        <Icon className={cn("h-[22px] w-[22px]", isActive && "stroke-[2.4]")} />
        <span>{label}</span>
      </>
    )}
  </NavLink>
);

export const MobileBottomNav = ({ onNew, onMore, moreActive = false, className }) => (
  <nav
    data-testid="mobile-bottom-nav"
    aria-label="Hovudnavigasjon"
    className={cn("fixed inset-x-0 bottom-0 z-[var(--z-sticky)] px-3 pb-[max(var(--safe-bottom),10px)] lg:hidden", className)}
  >
    <div className="mx-auto max-w-[520px] rounded-[22px] border bg-background/80 shadow-[0_10px_30px_rgba(0,0,0,0.10)] backdrop-blur-xl supports-[backdrop-filter]:bg-background/70 dark:shadow-[0_10px_30px_rgba(0,0,0,0.45)]">
      <div className="grid h-[var(--mobile-nav-h)] grid-cols-5 items-stretch px-1">
        <Item to="/" end icon={LayoutDashboard} label="Oversikt" testid="bottom-nav-dashboard" />
        <Item to="/kalender" icon={CalendarDays} label="Kalender" testid="bottom-nav-kalender" />

        {/* Centre: raised primary action */}
        <button
          type="button"
          onClick={onNew}
          data-testid="bottom-nav-new-report"
          aria-label="Ny rapport"
          className="pressable relative flex h-full flex-col items-center justify-end pb-2 text-[11px] font-semibold leading-none text-foreground focus-visible:outline-none"
        >
          <span
            className={cn(
              "absolute left-1/2 top-0 grid h-14 w-14 -translate-x-1/2 -translate-y-[14px] place-items-center rounded-[18px] bg-primary text-primary-foreground",
              "shadow-[0_14px_30px_rgba(255,78,0,0.35)] ring-4 ring-background transition-transform duration-150"
            )}
          >
            <Plus className="h-7 w-7 stroke-[2.5]" />
          </span>
          <span>Ny</span>
        </button>

        <Item to="/maskiner" icon={Wrench} label="Maskiner" testid="bottom-nav-maskiner" />
        <button type="button" onClick={onMore} data-testid="bottom-nav-meir" aria-label="Meir" aria-expanded={moreActive} className={itemClass(moreActive)}>
          {moreActive && <Indicator />}
          <Menu className={cn("h-[22px] w-[22px]", moreActive && "stroke-[2.4]")} />
          <span>Meir</span>
        </button>
      </div>
    </div>
  </nav>
);
